// src/hooks/user/useDebouncedFollowAction.js
import { useCallback, useRef, useMemo } from "react";
import { useFollowUser } from "./useFollowUser";
import { useUnfollowUser } from "./useUnfollowUser";

/**
 * Custom hook for debounced follow/unfollow actions
 *
 * Prevents rapid clicking and multiple simultaneous requests by debouncing
 * follow/unfollow actions. Includes intelligent debouncing that adapts to user behavior.
 *
 * Requirements: 8.1, 8.2 - Optimistic updates and debouncing for follow actions
 *
 * @param {number} debounceMs - Base debounce delay in milliseconds (default: 300)
 * @param {Object} options - Configuration options
 * @param {boolean} options.adaptiveDebounce - Whether to use adaptive debouncing (default: true)
 * @param {number} options.maxDebounce - Maximum debounce delay (default: 1000)
 * @returns {Object} Debounced follow/unfollow functions and loading states
 */
export const useDebouncedFollowAction = (debounceMs = 300, options = {}) => {
  const { adaptiveDebounce = true, maxDebounce = 1000 } = options;

  const followMutation = useFollowUser();
  const unfollowMutation = useUnfollowUser();
  const timeoutRef = useRef(null);
  const lastActionRef = useRef(null);
  const actionCountRef = useRef(0);
  const lastActionTimeRef = useRef(0);

  // Calculate adaptive debounce delay based on user behavior
  const getDebounceDelay = useCallback(() => {
    if (!adaptiveDebounce) return debounceMs;

    const now = Date.now();
    const timeSinceLastAction = now - lastActionTimeRef.current;

    // If user is clicking rapidly (within 2 seconds), increase debounce
    if (timeSinceLastAction < 2000) {
      actionCountRef.current += 1;
      const adaptiveDelay = Math.min(
        debounceMs * (1 + actionCountRef.current * 0.5),
        maxDebounce
      );
      return adaptiveDelay;
    } else {
      // Reset counter if user paused
      actionCountRef.current = 0;
      return debounceMs;
    }
  }, [debounceMs, adaptiveDebounce, maxDebounce]);

  // Clear any pending timeout
  const clearPendingAction = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Enhanced debounced follow action
  const debouncedFollow = useCallback(
    (userId) => {
      // Clear any pending action
      clearPendingAction();

      // Prevent duplicate actions
      const actionKey = `follow-${userId}`;
      if (lastActionRef.current === actionKey) {
        return;
      }

      lastActionRef.current = actionKey;
      lastActionTimeRef.current = Date.now();

      const delay = getDebounceDelay();

      timeoutRef.current = setTimeout(() => {
        followMutation.mutate(userId, {
          onSettled: () => {
            lastActionRef.current = null;
          },
        });
      }, delay);
    },
    [followMutation, clearPendingAction, getDebounceDelay]
  );

  // Enhanced debounced unfollow action
  const debouncedUnfollow = useCallback(
    (userId) => {
      // Clear any pending action
      clearPendingAction();

      // Prevent duplicate actions
      const actionKey = `unfollow-${userId}`;
      if (lastActionRef.current === actionKey) {
        return;
      }

      lastActionRef.current = actionKey;
      lastActionTimeRef.current = Date.now();

      const delay = getDebounceDelay();

      timeoutRef.current = setTimeout(() => {
        unfollowMutation.mutate(userId, {
          onSettled: () => {
            lastActionRef.current = null;
          },
        });
      }, delay);
    },
    [unfollowMutation, clearPendingAction, getDebounceDelay]
  );

  // Combined toggle action with smart state detection
  const toggleFollow = useCallback(
    (userId, isCurrentlyFollowing) => {
      // Cancel any pending opposite action for the same user
      const oppositeAction = isCurrentlyFollowing
        ? `follow-${userId}`
        : `unfollow-${userId}`;
      if (lastActionRef.current === oppositeAction) {
        clearPendingAction();
        lastActionRef.current = null;
      }

      if (isCurrentlyFollowing) {
        debouncedUnfollow(userId);
      } else {
        debouncedFollow(userId);
      }
    },
    [debouncedFollow, debouncedUnfollow, clearPendingAction]
  );

  // Immediate action for critical scenarios (bypass debounce)
  const immediateToggle = useCallback(
    (userId, isCurrentlyFollowing) => {
      clearPendingAction();
      lastActionRef.current = null;

      if (isCurrentlyFollowing) {
        unfollowMutation.mutate(userId);
      } else {
        followMutation.mutate(userId);
      }
    },
    [followMutation, unfollowMutation, clearPendingAction]
  );

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    clearPendingAction();
    lastActionRef.current = null;
    actionCountRef.current = 0;
    lastActionTimeRef.current = 0;
  }, [clearPendingAction]);

  // Memoize return object to prevent unnecessary re-renders
  const returnValue = useMemo(
    () => ({
      followUser: debouncedFollow,
      unfollowUser: debouncedUnfollow,
      toggleFollow,
      immediateToggle,
      isFollowLoading: followMutation.isLoading,
      isUnfollowLoading: unfollowMutation.isLoading,
      isLoading: followMutation.isLoading || unfollowMutation.isLoading,
      followError: followMutation.error,
      unfollowError: unfollowMutation.error,
      hasPendingAction: !!timeoutRef.current,
      cleanup,
    }),
    [
      debouncedFollow,
      debouncedUnfollow,
      toggleFollow,
      immediateToggle,
      followMutation.isLoading,
      unfollowMutation.isLoading,
      followMutation.error,
      unfollowMutation.error,
      cleanup,
    ]
  );

  return returnValue;
};

export default useDebouncedFollowAction;
